// A run's dungeon: every level built up front from the run's seed, then wired
// together so the party can climb and descend between them.
//
// Levels are linked through their staircases. Each staircase knows the level
// and square it leads to, and its opposite number leads straight back. Some
// levels also hold a sealed vault: a walled chamber with no way in from that
// level, reachable only by a second staircase on the level below. That makes
// the dungeon a 3D space rather than a stack of separate maps.

import generateRandomMap from './mapGenerator';
import { spawnMonsters, findFloorSpot } from './combat/monsters';
import { createExplored, revealAround } from './fogOfWar';
import { withSeed, seededRandom, levelSeed } from './rng';
import { WALL_SETS, FLOORS, CEILINGS, TOWN_THEME } from './textures';
import { buildTown, townName, TOWN_SIZE } from './town';
import { createStock } from './services';

// Levels are 40x40; the minimap shows a window around the party
export const LEVEL_SIZE = 40;

export const TILE = {
    floor: 0,
    stairsUp: 7,
    chest: 8,
    trainer: 9,
    stairsDown: 10,
    brokenDoor: 11,
    // The town's trades, each a doorway painted on the building behind it
    magicSchool: 12,
    market: 13,
    temple: 14,
    tavern: 15,
};

// Which square belongs to which trade, both ways round
export const FACILITY_TILES = {
    training: TILE.trainer,
    magic: TILE.magicSchool,
    market: TILE.market,
    temple: TILE.temple,
    tavern: TILE.tavern,
};

export const TILE_FACILITIES = Object.fromEntries(
    Object.entries(FACILITY_TILES).map(([trade, tile]) => [tile, trade])
);

// Every run begins in a town, on the level above the dungeon's first floor.
// A single-level run is all dungeon: there would be nowhere to go from a town.
export const TOWN_LEVEL = 1;

export function hasTown(levelCount) {
    return levelCount > 1;
}

// Roughly a third of doors are locked, and must be bashed in
const LOCKED_DOOR_CHANCE = 0.35;

// A trainer keeps a desk on every third level
export const TRAINER_EVERY = 3;
// How often a level hides a sealed vault
const VAULT_CHANCE = 0.55;

const key = (x, y) => `${x},${y}`;
const isWall = (tile) => tile === 1 || tile === 2;

function findTile(tiles, wanted) {
    for (let y = 0; y < tiles.length; y++) {
        for (let x = 0; x < tiles[0].length; x++) {
            if (tiles[y][x] === wanted) return [x, y];
        }
    }
    return null;
}

const DIRECTIONS = [[0, -1], [1, 0], [0, 1], [-1, 0]];

// Which way the wall lies from this square, or null if it stands in the open.
// Stairways are cut into a wall, so they need one.
export function wallBeside(tiles, [x, y]) {
    for (const [dx, dy] of DIRECTIONS) {
        const row = tiles[y + dy];
        if (row && isWall(row[x + dx])) return [dx, dy];
    }
    return null;
}

// The nearest walkable square that has a wall to set a stairway into
function spotAgainstWall(tiles, from) {
    if (wallBeside(tiles, from)) return from;
    const seen = new Set([from.join(',')]);
    const queue = [from];
    while (queue.length) {
        const [x, y] = queue.shift();
        for (const [dx, dy] of DIRECTIONS) {
            const next = [x + dx, y + dy];
            const tile = tiles[next[1]] && tiles[next[1]][next[0]];
            if (tile === undefined || seen.has(next.join(','))) continue;
            seen.add(next.join(','));
            if (tile !== TILE.floor) continue;
            if (wallBeside(tiles, next)) return next;
            queue.push(next);
        }
    }
    return from;
}

// Move a stairway onto a square that has a wall behind it
function againstWall(level, pos) {
    const spot = spotAgainstWall(level.tiles, pos);
    if (spot !== pos && level.tiles[pos[1]][pos[0]] === TILE.stairsUp) {
        level.tiles[pos[1]][pos[0]] = TILE.floor;   // leave no orphan staircase
    }
    return spot;
}

// Walking distance to every square. Doors count as passable, since the party
// can open or bash them; only rock blocks the way.
function walkDistances(tiles, [sx, sy]) {
    const dist = tiles.map((row) => row.map(() => Infinity));
    dist[sy][sx] = 0;
    const queue = [[sx, sy]];
    while (queue.length) {
        const [x, y] = queue.shift();
        for (const [dx, dy] of DIRECTIONS) {
            const nx = x + dx;
            const ny = y + dy;
            const tile = tiles[ny] && tiles[ny][nx];
            if (tile === undefined || isWall(tile) || dist[ny][nx] !== Infinity) continue;
            dist[ny][nx] = dist[y][x] + 1;
            queue.push([nx, ny]);
        }
    }
    return dist;
}

// The generator drops the stairs and the key chest in any room it likes, which
// often leaves them a few paces from where the party arrives. Move them so the
// level has to be crossed: the stairs go somewhere remote, and the key waits
// well away from both the entrance and the stairs.
const STAIRS_MIN_SHARE = 0.6;   // of the furthest square on the level
const KEY_MIN_FROM_START = 10;
const KEY_MIN_FROM_STAIRS = 8;

function spaceOutFurniture(level, rng) {
    const { tiles, start } = level;
    const fromStart = walkDistances(tiles, start);
    const reached = fromStart.flat().filter((d) => d !== Infinity);
    if (!reached.length) return;
    const furthest = Math.max(...reached);

    const squares = (test) => {
        const found = [];
        for (let y = 1; y < tiles.length - 1; y++) {
            for (let x = 1; x < tiles[0].length - 1; x++) {
                if (test(x, y)) found.push([x, y]);
            }
        }
        return found;
    };

    // Somewhere remote, against a wall, for the way up
    const old = findTile(tiles, TILE.stairsUp);
    const open = (x, y) => tiles[y][x] === TILE.floor || (old && x === old[0] && y === old[1]);
    let candidates = squares((x, y) => open(x, y) && fromStart[y][x] !== Infinity &&
                                       wallBeside(tiles, [x, y]) &&
                                       fromStart[y][x] >= furthest * STAIRS_MIN_SHARE);
    if (!candidates.length) {
        // Nothing that remote: take the furthest square with a wall behind it
        candidates = squares((x, y) => open(x, y) && fromStart[y][x] !== Infinity && wallBeside(tiles, [x, y]));
        candidates.sort((a, b) => fromStart[b[1]][b[0]] - fromStart[a[1]][a[0]]);
        candidates = candidates.slice(0, 3);
    }
    if (candidates.length) {
        const chosen = candidates[Math.floor(rng() * candidates.length)];
        if (old) tiles[old[1]][old[0]] = TILE.floor;
        tiles[chosen[1]][chosen[0]] = TILE.stairsUp;
        level.stairsUpAt = chosen;
    }

    // The key: as far from both the entrance and the stairs as the chests allow
    const stairs = level.stairsUpAt || old;
    if (!stairs) return;
    const fromStairs = walkDistances(tiles, stairs);
    const apartness = ([x, y]) => Math.min(fromStart[y][x], fromStairs[y][x]);

    const chests = squares((x, y) => tiles[y][x] === TILE.chest && fromStart[y][x] !== Infinity);
    chests.sort((a, b) => apartness(b) - apartness(a));
    let keyChest = chests[0];

    if (!keyChest || fromStart[keyChest[1]][keyChest[0]] < KEY_MIN_FROM_START ||
                     fromStairs[keyChest[1]][keyChest[0]] < KEY_MIN_FROM_STAIRS) {
        const spots = squares((x, y) => tiles[y][x] === TILE.floor &&
                                        fromStart[y][x] >= KEY_MIN_FROM_START &&
                                        fromStairs[y][x] >= KEY_MIN_FROM_STAIRS);
        if (spots.length) {
            const moved = spots[Math.floor(rng() * spots.length)];
            if (keyChest) tiles[keyChest[1]][keyChest[0]] = TILE.floor;
            tiles[moved[1]][moved[0]] = TILE.chest;
            keyChest = moved;
        }
    }
    level.keyChestLocation = keyChest ? { x: keyChest[0], y: keyChest[1] } : null;
}

// Squares reachable on foot from a starting square, ignoring closed doors
export function reachableFrom(tiles, [sx, sy]) {
    const seen = tiles.map((row) => row.map(() => false));
    const walkable = (tile) => tile === 0 || tile === 4 || tile === 6 || tile >= 7;
    if (!walkable(tiles[sy][sx])) return seen;
    seen[sy][sx] = true;
    const queue = [[sx, sy]];
    while (queue.length) {
        const [x, y] = queue.shift();
        [[0, -1], [1, 0], [0, 1], [-1, 0]].forEach(([dx, dy]) => {
            const nx = x + dx;
            const ny = y + dy;
            if (ny < 0 || ny >= tiles.length || nx < 0 || nx >= tiles[0].length) return;
            if (seen[ny][nx] || !walkable(tiles[ny][nx])) return;
            seen[ny][nx] = true;
            queue.push([nx, ny]);
        });
    }
    return seen;
}

// Carve a sealed chamber inside a solid block of rock, so nothing on this
// level can reach it. Tries a roomy chamber first, then smaller ones, since
// open maps rarely have a big enough block. Returns the squares carved.
const VAULT_SHAPES = [
    { block: [4, 4], inner: [2, 2] },
    { block: [4, 3], inner: [2, 1] },
    { block: [3, 4], inner: [1, 2] },
    { block: [3, 3], inner: [1, 1] },
];

function carveVault(tiles, rng) {
    const height = tiles.length;
    const width = tiles[0].length;

    for (const { block, inner } of VAULT_SHAPES) {
        const [bw, bh] = block;
        const spots = [];
        for (let y = 1; y <= height - bh - 1; y++) {
            for (let x = 1; x <= width - bw - 1; x++) {
                let solid = true;
                for (let dy = 0; dy < bh && solid; dy++) {
                    for (let dx = 0; dx < bw && solid; dx++) {
                        if (!isWall(tiles[y + dy][x + dx])) solid = false;
                    }
                }
                if (solid) spots.push([x, y]);
            }
        }
        if (!spots.length) continue;

        const [vx, vy] = spots[Math.floor(rng() * spots.length)];
        const carved = [];
        for (let dy = 1; dy <= inner[1]; dy++) {
            for (let dx = 1; dx <= inner[0]; dx++) {
                tiles[vy + dy][vx + dx] = TILE.floor;
                carved.push([vx + dx, vy + dy]);
            }
        }
        return carved;
    }
    return null;
}

// What a level is built of. Consecutive levels always differ, so the change
// is obvious when the party climbs.
function pickTheme(rng, previous) {
    const wallSets = Object.keys(WALL_SETS);
    for (let attempt = 0; attempt < 8; attempt++) {
        const theme = {
            walls: wallSets[Math.floor(rng() * wallSets.length)],
            floor: FLOORS[Math.floor(rng() * FLOORS.length)],
            ceiling: CEILINGS[Math.floor(rng() * CEILINGS.length)],
        };
        const same = previous && theme.walls === previous.walls &&
                     theme.floor === previous.floor && theme.ceiling === previous.ceiling;
        if (!same) return theme;
    }
    return { walls: wallSets[0], floor: FLOORS[0], ceiling: CEILINGS[0] };
}

// Level one: the town, with its trades and the gate down into the dungeon
function buildTownLevel(seed, level) {
    const rng = seededRandom(`${seed}:town`);
    const town = buildTown(TOWN_SIZE, rng);
    town.tiles[town.gate[1]][town.gate[0]] = TILE.stairsUp;

    // The shop doors stand shut but unlocked: the party walks in and out
    const doorStates = {};
    town.doors.forEach((door) => {
        doorStates[key(door.x, door.y)] = {
            isOpen: false,
            orientation: door.orientation,
            locked: false,
            maxHp: 10,
            hp: 10,
            endurance: 1,
        };
    });

    return {
        level,
        town: true,
        name: townName(rng),
        theme: TOWN_THEME,
        tiles: town.tiles,
        // Which buildings carry which trade's front beside their door, and
        // what the streets and the shops are floored with
        surrounds: town.surrounds,
        floors: town.floors,
        // Towns differ in how they let you down into the dungeon
        entranceStyle: town.entranceStyle,
        gate: town.gate,
        start: town.start_position,
        doorStates,
        keyChestLocation: null,
        monsters: [],
        // The party knows its own town: nothing here is hidden
        explored: town.tiles.map((row) => row.map(() => true)),
        hasKey: false,
        // What the merchant has on the shelves this run
        shop: createStock(rng),
        stairs: {},
    };
}

// Build every level of a run, then wire the staircases together.
//
// `townLevel` is a town painted in the map editor, ready made (see
// maps.js); without one the town is generated from the seed like everything
// else. It arrives built rather than as a map so that the editor's own
// module stays out of the generator's way.
export function buildDungeon(seed, levelCount, townLevel = null) {
    const levels = [];

    for (let level = 1; level <= levelCount; level++) {
        if (level === TOWN_LEVEL && hasTown(levelCount)) {
            levels.push(townLevel ? { ...townLevel, level } : buildTownLevel(seed, level));
            continue;
        }
        const built = withSeed(levelSeed(seed, level), () => {
            const map = generateRandomMap(LEVEL_SIZE, LEVEL_SIZE, 1);
            // Roughly one monster per 60 walkable squares
            const walkable = map.tiles.flat().filter((tile) => tile === 0).length;
            const monsters = spawnMonsters(map.tiles, map.start_position, Math.random,
                                           Math.max(4, Math.min(16, Math.round(walkable / 60))));
            if (level % TRAINER_EVERY === 0) {
                const desk = findFloorSpot(map.tiles, map.start_position, 4, Math.random);
                if (desk && map.tiles[desk[1]][desk[0]] === TILE.floor) {
                    map.tiles[desk[1]][desk[0]] = TILE.trainer;
                }
            }
            return { map, monsters };
        });

        // Doors: some locked, and sturdier the deeper the level
        const doorRng = seededRandom(`${seed}:doors:${level}`);
        const doorStates = {};
        (built.map.doors || []).forEach((door) => {
            const locked = doorRng() < LOCKED_DOOR_CHANCE;
            if (locked) {
                // A locked door is shut, whatever the generator decided
                built.map.tiles[door.y][door.x] = door.orientation === 'horizontal' ? 3 : 5;
            }
            doorStates[key(door.x, door.y)] = {
                isOpen: locked ? false : door.isOpen,
                orientation: door.orientation,
                locked,
                maxHp: Math.round(14 + level * 2 + doorRng() * 12),
                hp: 0,          // filled in below
                endurance: Math.round(2 + doorRng() * 5),
            };
            doorStates[key(door.x, door.y)].hp = doorStates[key(door.x, door.y)].maxHp;
        });

        const record = {
            level,
            theme: pickTheme(seededRandom(`${seed}:theme:${level}`), levels.length ? levels[levels.length - 1].theme : null),
            tiles: built.map.tiles,
            start: built.map.start_position,
            doorStates,
            keyChestLocation: built.map.keyChestLocation || null,
            monsters: built.monsters,
            explored: createExplored(built.map.tiles),
            hasKey: false,
            stairs: {},
        };
        // Spread the stairs and the key across the level before anything is linked
        spaceOutFurniture(record, seededRandom(`${seed}:layout:${level}`));
        levels.push(record);
    }

    linkLevels(levels, seed, levelCount);
    // The party begins knowing what it can see from the entrance
    levels[0].explored = revealAround(levels[0].explored, levels[0].tiles, levels[0].start);
    return levels;
}

function addStairs(level, [x, y], tile, link) {
    level.tiles[y][x] = tile;
    level.stairs[key(x, y)] = link;
}

// Join each level to the one above: the main staircase, and sometimes a
// second one leading into a sealed vault
function linkLevels(levels, seed, levelCount) {
    levels.forEach((level, index) => {
        const upper = levels[index + 1];
        const found = findTile(level.tiles, TILE.stairsUp);
        const upStairs = found ? againstWall(level, found) : null;

        // The last level's staircase leads out of the dungeon
        if (!upper) {
            if (upStairs) {
                addStairs(level, upStairs, TILE.stairsUp, { toLevel: levelCount + 1, exit: true, locked: true });
            }
            return;
        }

        // Arriving on the level above puts the party on its own way back down
        const landing = spotAgainstWall(upper.tiles, upper.start);
        addStairs(upper, landing, TILE.stairsDown, {
            toLevel: level.level,
            toPos: upStairs,
            locked: false,
        });
        if (upStairs) {
            addStairs(level, upStairs, TILE.stairsUp, {
                toLevel: upper.level,
                toPos: landing,
                // The town's gate stands open; below, the way on must be unlocked
                locked: !level.town,
            });
        }

        // A sealed vault on the upper level, entered from this one. Never from
        // the town: its streets hold nothing but the trades.
        if (level.town) return;
        const rng = seededRandom(`${seed}:vault:${upper.level}`);
        if (rng() > VAULT_CHANCE) return;
        const carved = withSeed(`${seed}:vault:${upper.level}:carve`, () => carveVault(upper.tiles, Math.random));
        if (!carved) return;

        const chosen = findFloorSpot(level.tiles, level.start, 4, rng);
        if (!chosen || level.tiles[chosen[1]][chosen[0]] !== TILE.floor) return;
        const doorway = spotAgainstWall(level.tiles, chosen);
        if (level.tiles[doorway[1]][doorway[0]] !== TILE.floor) return;

        const [vaultStairs, vaultChest] = carved;
        if (!vaultStairs) return;
        addStairs(level, doorway, TILE.stairsUp, {
            toLevel: upper.level,
            toPos: vaultStairs,
            locked: false,
            vault: true,
        });
        addStairs(upper, vaultStairs, TILE.stairsDown, {
            toLevel: level.level,
            toPos: doorway,
            locked: false,
            vault: true,
        });
        // A roomier chamber also holds something worth the trip
        if (vaultChest) upper.tiles[vaultChest[1]][vaultChest[0]] = TILE.chest;
    });
}

// What the party can see of a level when it arrives
export function levelOnArrival(record, at) {
    return revealAround(record.explored, record.tiles, at);
}
